import { GameSession, GameResult, GameStatistics } from '../types';
import { LocalStorageManager } from './LocalStorageManager';

export class OfflineManager {
  private localStorageManager: LocalStorageManager;
  private isOnline: boolean;
  private onlineStatusCallbacks: ((isOnline: boolean) => void)[] = [];

  constructor() {
    this.localStorageManager = new LocalStorageManager();
    this.isOnline = navigator.onLine;
    this.setupOnlineStatusListeners();
  }

  /**
   * Check if the app is currently online
   */
  getOnlineStatus(): boolean {
    return this.isOnline;
  }

  /**
   * Subscribe to online status changes
   */
  onOnlineStatusChange(callback: (isOnline: boolean) => void): () => void {
    this.onlineStatusCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onlineStatusCallbacks.indexOf(callback);
      if (index > -1) {
        this.onlineStatusCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Save game session for offline access
   */
  async saveOfflineGame(gameSession: GameSession): Promise<void> {
    try {
      // Save to localStorage
      this.localStorageManager.saveOfflineGame(gameSession);
      
      // Also cache in service worker if available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_GAME_STATE',
          gameState: gameSession
        });
      }
    } catch (error) {
      console.error('Failed to save offline game:', error);
      throw new Error('Failed to save game for offline access');
    }
  }

  /**
   * Get offline games from storage
   */
  getOfflineGames(): GameSession[] {
    try {
      return this.localStorageManager.getOfflineGames();
    } catch (error) {
      console.error('Failed to load offline games:', error);
      return [];
    }
  }

  /**
   * Remove offline game from storage
   */
  removeOfflineGame(gameId: string): void {
    try {
      this.localStorageManager.removeOfflineGame(gameId);
    } catch (error) {
      console.error('Failed to remove offline game:', error);
    }
  }

  /**
   * Record game result (works offline)
   */
  recordGameResult(gameResult: GameResult): GameStatistics {
    try {
      return this.localStorageManager.recordGameResult(gameResult);
    } catch (error) {
      console.error('Failed to record game result:', error);
      throw new Error('Failed to record game result');
    }
  }

  /**
   * Get game statistics (works offline)
   */
  getStatistics(): GameStatistics {
    try {
      return this.localStorageManager.getStatistics();
    } catch (error) {
      console.error('Failed to load statistics:', error);
      return this.localStorageManager.getStatistics(); // Return defaults
    }
  }

  /**
   * Check if single-player functionality is available offline
   */
  isSinglePlayerAvailable(): boolean {
    // Single-player should always be available as it doesn't require network
    return true;
  }

  /**
   * Check if multiplayer functionality is available
   */
  isMultiplayerAvailable(): boolean {
    // Multiplayer requires network connection
    return this.isOnline;
  }

  /**
   * Get cached games from service worker
   */
  async getCachedGamesFromServiceWorker(): Promise<GameSession[]> {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      return [];
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.games || []);
      };

      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(
          { type: 'GET_CACHED_GAMES' },
          [messageChannel.port2]
        );
      } else {
        resolve([]);
      }

      // Timeout after 5 seconds
      setTimeout(() => resolve([]), 5000);
    });
  }

  /**
   * Sync offline data when coming back online
   */
  async syncOfflineData(): Promise<void> {
    if (!this.isOnline) {
      return;
    }

    try {
      // In a real implementation, this would sync with the backend
      // For now, we just ensure local data is consistent
      const offlineGames = this.getOfflineGames();
      console.log(`Syncing ${offlineGames.length} offline games`);
      
      // Here you would typically:
      // 1. Send offline game results to the server
      // 2. Update statistics on the server
      // 3. Resolve any conflicts
      // 4. Clean up successfully synced offline data
      
      // For this implementation, we'll just log the sync attempt
      console.log('Offline data sync completed');
    } catch (error) {
      console.error('Failed to sync offline data:', error);
    }
  }

  /**
   * Clear all offline data
   */
  clearOfflineData(): void {
    try {
      this.localStorageManager.clearAllData();
    } catch (error) {
      console.error('Failed to clear offline data:', error);
    }
  }

  /**
   * Check if localStorage is available for offline storage
   */
  isOfflineStorageAvailable(): boolean {
    return this.localStorageManager.isStorageAvailable();
  }

  /**
   * Get offline storage usage information
   */
  getOfflineStorageInfo(): { gamesCount: number; hasProfile: boolean } {
    try {
      const games = this.getOfflineGames();
      const profile = this.localStorageManager.getProfile();
      
      return {
        gamesCount: games.length,
        hasProfile: profile !== null
      };
    } catch (error) {
      console.error('Failed to get offline storage info:', error);
      return { gamesCount: 0, hasProfile: false };
    }
  }

  /**
   * Setup online/offline status listeners
   */
  private setupOnlineStatusListeners(): void {
    const updateOnlineStatus = () => {
      const wasOnline = this.isOnline;
      this.isOnline = navigator.onLine;
      
      if (wasOnline !== this.isOnline) {
        // Notify all callbacks about status change
        this.onlineStatusCallbacks.forEach(callback => {
          try {
            callback(this.isOnline);
          } catch (error) {
            console.error('Error in online status callback:', error);
          }
        });

        // If coming back online, attempt to sync
        if (this.isOnline && !wasOnline) {
          this.syncOfflineData().catch(error => {
            console.error('Auto-sync failed:', error);
          });
        }
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
  }
}